import React, { useCallback } from "react";
import DivShadow from "@components/layout/DivShadow";
import VStack from "@components/layout/VStack";
import TitlePage from "@components/layout/TitlePage";
import { titlePages } from "@shared/constants/appConstants";
import { useAppConfigStore, useOutlookStore, useSettingStore, useThemeStore } from "@shared/stores";
import LocationSetting from "@features/settings/components/LocationSetting";
import DefaultResourceSetting from "@features/settings/components/DefaultResourceSetting";
import DefaultCategoriesSetting from "@features/settings/components/DefaultCategoriesSetting";
// import QuickSetting from "@features/settings/components/QuickSetting";
import AssistantOrganizerSetting from "@features/settings/components/AssistantOrganizerSetting";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { OrganizerSettingEntity } from "@features/settings/services/entities/organizerSettingEntity";
import { useGlobalContext } from "@shared/contexts/GlobalContext";
import TileConfigurationSetting from "@features/settings/components/TileConfigurationSetting";
import { settingLabels } from "@shared/constants/setting";
import SwitchSetting from "@components/ui/SwitchSetting";
import HelpSetting from "../components/HelpSetting";
import { useTranslation } from "react-i18next";
import { DesktopCursorIcon, PhotoFilterIcon, SparkleIcon } from "@/pages/taskpane/components/icons/IconCustom";
import DateTimeSetting from "../components/DateTimeSetting";

const Setting = () => {
  const { t } = useTranslation();
  const { setIsLoadingOverlay } = useGlobalContext();
  // const showToast = useToastStore((state) => state.showToast);
  const { isDarkMode } = useThemeStore();
  const { organizer } = useOutlookStore();
  const { recommendationScreen, capacityFilter, aiAssistant } = useSettingStore();
  const { parameters } = useAppConfigStore();

  const { mutate: updateSettingMutation } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        useSettingStore.setState({
          recommendationScreen: data.recommendationScreen,
          capacityFilter: data.capacityFilter,
          aiAssistant: data.aiAssistant,
        });

        // const oldData = queryClient.getQueryData([
        //   QUERY_KEY.GET_ORGANIZER_SETTING,
        //   organizer,
        // ]) as OrganizerSettingEntity;
        // showToast(t("Setting updated successfully"), true);
        // queryClient.setQueryData([QUERY_KEY.GET_ORGANIZER_SETTING, organizer], (oldData: OrganizerSettingEntity[]) => {
        //   return {
        //     ...oldData,
        //     recommendationScreen: data.recommendationScreen,
        //     capacityFilter: data.capacityFilter,
        //     aiAssistant: data.aiAssistant,
        //   };
        // });
      },
      onError: () => {
        // showToast(t("Setting updated failed"), false);
      },
      onSettled: () => {
        setIsLoadingOverlay(false);
      },
    },
  });

  // Toggle Recommendation Screen

  const handleToggleRecommendation = useCallback(() => {
    if (!organizer) return;
    setIsLoadingOverlay(true);
    updateSettingMutation({
      organizerEmail: organizer,
      recommendationScreen: !recommendationScreen,
    });
  }, [organizer, recommendationScreen, updateSettingMutation]);

  // Toggle Capacity Filter

  const handleToggleCapacityFilter = useCallback(() => {
    if (!organizer) return;
    setIsLoadingOverlay(true);
    updateSettingMutation({
      organizerEmail: organizer,
      capacityFilter: !capacityFilter,
    });
  }, [organizer, capacityFilter, updateSettingMutation]);

  // Toggle AI Assistant

  const handleToggleAIAssistant = useCallback(() => {
    if (!organizer) return;
    setIsLoadingOverlay(true);
    updateSettingMutation({
      organizerEmail: organizer,
      aiAssistant: !aiAssistant,
    });
  }, [organizer, aiAssistant, updateSettingMutation]);

  return (
    <VStack className="pt-1 h-full overflow-hidden" gap={3}>
      <TitlePage className="px-4" title={t(titlePages.setting.title)} />

      <VStack gap={3} className="px-4 pt-1 pb-4 flex-1 overflow-y-auto no-scrollbar" alignItems="center">
        {/*  Assistant Organizer */}
        {parameters?.resourceFinderAssistantOrganizer ? (
          <WrapperCommon>
            <AssistantOrganizerSetting />
            <p className="mt-3 text-[13px] text-justify">
              <span className="font-semibold">{t("Note:")}</span>{" "}
              {t(
                "An Assistant Organizer can receive the same emails from Resource Central as the organizer and can handle orders on behalf of the organizer."
              )}
            </p>
          </WrapperCommon>
        ) : null}

        {/* DateTime Setting */}
        <WrapperCommon>
          <DateTimeSetting />
        </WrapperCommon>

        {/* Default Location */}
        <WrapperCommon>
          <LocationSetting />
        </WrapperCommon>

        {/* Default resource type */}
        <WrapperCommon>
          <DefaultResourceSetting />
        </WrapperCommon>

        {/* Default categories */}
        <WrapperCommon>
          <DefaultCategoriesSetting />
        </WrapperCommon>

        {/* Quick button */}
        {/* <WrapperCommon>
          <QuickSetting />
        </WrapperCommon> */}

        {/* Tile configuration */}
        <WrapperCommon>
          <TileConfigurationSetting />
        </WrapperCommon>

        {/* Recommendation screen */}
        <SwitchSetting
          icon={<DesktopCursorIcon isDarkMode={isDarkMode} />}
          label={t(settingLabels.recommendation)}
          isChecked={recommendationScreen}
          onAction={handleToggleRecommendation}
        />

        {/* Capacity fillter */}
        <SwitchSetting
          icon={<PhotoFilterIcon isDarkMode={isDarkMode} />}
          label={t(settingLabels.capacityFilter)}
          isChecked={capacityFilter}
          onAction={handleToggleCapacityFilter}
        />

        {/* AI Assitant */}
        <SwitchSetting
          icon={<SparkleIcon isDarkMode={isDarkMode} />}
          label={t(settingLabels.aiAssistant)}
          isChecked={aiAssistant}
          onAction={handleToggleAIAssistant}
        />

        {/* Help */}
        <WrapperCommon>
          <HelpSetting />
        </WrapperCommon>

        {/* Version */}
        <p className="mt-1 text-darkerGray text-xs dark:text-softGray">
          @Outlook Add-in Version. 2.0.42 (SR2 1.1 - 01/26/2026)
        </p>
      </VStack>
    </VStack>
  );
};

export default Setting;

const WrapperCommon = ({ children }: { children: React.ReactNode }) => {
  return (
    <DivShadow className="rounded-[4px] p-3 bg-white dark:bg-darkBackgroundColor dark:shadow-none dark:border dark:border-darkMediumGray">
      {children}
    </DivShadow>
  );
};
